// ---------------------------------------------------------------------------
// chartingService
//
// Frontend data layer for the end-of-day charting queue. Talks to the backend
// aggregation routes (`/api/v1/charting/*`) which join the provider's scribe
// sessions (`Conversation`) and telephone encounters (`TelephonicConversation`)
// into a single "what still owes the EHR a note" list.
//
// All push / generate / dismiss state is denormalized onto the session docs at
// action time (see patientService + telephoneEncounterService), so the queue
// is a cheap indexed read rather than a fan-out across Messages subcollections.
// ---------------------------------------------------------------------------

import {request} from './apiClient';

const CHARTING_PREFIX = '/api/v1/charting';

export type ChartingStatus =
  | 'needs_note'
  | 'draft'
  | 'pushed'
  | 'complete'
  | 'needs_repush';
// How the visit was delivered (drives the neutral Phone / Video / In-person
// pill, matching the rest of the app).
export type ChartingModality = 'in_person' | 'phone' | 'video';

// Lifecycle of a background EHR push, surfaced per-note in the list, inline on
// the note, and in the corner status dock. Kept in the data layer so both the
// push manager and the pure list component can share it without a cycle.
export type EhrPushPhase = 'pushing' | 'success' | 'partial' | 'error';

export interface ChartingQueueItem {
  conversationId: string;
  modality: ChartingModality;
  // Raw workflow type, so the UI can tell scribe (the given) from an
  // interpreter session via the app's resolveEncounterModality helper.
  workflowType: string | null;
  patientName: string | null;
  patientId: string | null;
  ehrPatientId: string | null;
  startTime: string | null;
  minutes: number | null;
  templateType: string | null;
  summary: string | null;
  mrn: string | null;
  language: string | null;
  status: ChartingStatus;
  noteGeneratedAt: string | null;
  ehrPushedAt: string | null;
  completedAt: string | null;
}

export interface ChartingQueue {
  date: string;
  today: ChartingQueueItem[];
  backlog: ChartingQueueItem[];
  pendingCount: number;
}

// Fetch the charting queue for a given day (defaults to today on the server).
// Always sends the provider's IANA timezone so the server buckets "today" by
// the provider's local calendar — without it the day rolls over at UTC
// midnight (late afternoon/evening in the US), which knocked today's sessions
// into the backlog and made freshly pushed rows vanish on the next fetch.
export async function fetchChartingQueue(options?: {
  date?: string;
  includeBacklog?: boolean;
  signal?: AbortSignal;
}): Promise<ChartingQueue> {
  const data = await request(`${CHARTING_PREFIX}/queue`, {
    query: {
      date: options?.date,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      includeBacklog: options?.includeBacklog,
    },
    signal: options?.signal,
  });
  return {
    date: data?.date || '',
    today: Array.isArray(data?.today) ? data.today : [],
    backlog: Array.isArray(data?.backlog) ? data.backlog : [],
    pendingCount:
      typeof data?.pendingCount === 'number' ? data.pendingCount : 0,
  };
}

// Lightweight count-only fetch for the sidebar badge. Reuses the queue route
// (there's no cheaper aggregate yet) but only surfaces the pending count.
// NB: `includeBacklog: false` is not a valid way to slim this call down — the
// server derives pendingCount from the rows it returns, so skipping the
// backlog also drops backlog rows from the count (verified against the live
// API). A real fix needs a count-only endpoint server-side.
export async function fetchChartingPendingCount(
  signal?: AbortSignal,
): Promise<number> {
  const queue = await fetchChartingQueue({signal});
  return queue.pendingCount;
}

// Remove a session from the charting queue ("dismiss" / not applicable).
export async function dismissSession(conversationId: string): Promise<void> {
  await request(`${CHARTING_PREFIX}/${conversationId}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// Undo a dismissal (restores the session to the queue).
export async function undismissSession(conversationId: string): Promise<void> {
  await request(`${CHARTING_PREFIX}/${conversationId}/undismiss`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// Mark a session as charted/done without pushing (e.g. charted outside Opalite).
// Keeps the row visible, checked off, rather than removing it like dismiss.
export async function completeSession(conversationId: string): Promise<void> {
  await request(`${CHARTING_PREFIX}/${conversationId}/complete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// Undo a manual completion (returns the session to its pre-complete status).
export async function uncompleteSession(conversationId: string): Promise<void> {
  await request(`${CHARTING_PREFIX}/${conversationId}/uncomplete`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// Record that a session's documentation reached the EHR. `ehr_pushed_at` is a
// rules-protected Conversation field, so the stamp must land server-side —
// the client writing it directly is silently PERMISSION_DENIED, which left
// pushed rows resurfacing as drafts on the next queue fetch. Called by the
// note-push completion path (patientService.updatePushedSnapshotForSession);
// telephone-encounter filings are stamped by the TE write route itself.
export async function markSessionPushed(
  conversationId: string,
  options?: {signature?: string | null},
): Promise<void> {
  await request(`${CHARTING_PREFIX}/${conversationId}/mark-pushed`, {
    method: 'POST',
    body: JSON.stringify(
      options && options.signature !== undefined
        ? {signature: options.signature}
        : {},
    ),
  });
}
